import React, { useState } from "react"
import { motion, AnimatePresence } from "framer-motion"
import styled from "styled-components"
import { rem } from "polished"
import { media, useMedia } from "@sr/common"
import { SanityImg, SemiBdTitle5 } from "@sr/gatsby-theme-ui"

import EntryRelatedCard from "./CaseStudyEntryRelatedCard"

export interface CaseStudyEntryRelatedShape {
  relatedEntries: {
    node: {
      path: {
        current: string
      }
      intro: {
        intro: string
        alt: string
        snippet: string
        customerLogo: SanityImg
      }
      sidebar: {
        company: string
        headquarters: string
        industry: string
        productsUsed: [string]
      }
    }
  }[]
}

const variants = {
  enter: (direction: number) => {
    return {
      x: direction > 0 ? "50vw" : "-50vw",
      opacity: 0,
    }
  },
  center: {
    zIndex: 1,
    x: 0,
    opacity: 1,
  },
  exit: (direction: number) => {
    return {
      zIndex: 0,
      x: direction < 0 ? "50vw" : "-50vw",
      opacity: 0,
    }
  },
}

const swipeConfidenceThreshold = 10000
const swipePower = (offset: number, velocity: number) => {
  return Math.abs(offset) * velocity
}

const CaseStudyEntryRelated = ({
  relatedEntries,
}: CaseStudyEntryRelatedShape) => {
  const [[active, direction], setActive] = useState([0, 0])

  const isMobile: boolean = !useMedia(media.stMidUp)

  const Navigate = (newDirection: number) => {
    let requestedIndex = active + newDirection

    //check active slide vs requested slide. update active slide and animation direction in state
    if (requestedIndex < 0) {
      setActive([relatedEntries.length - 1, newDirection])
    } else if (requestedIndex > relatedEntries.length - 1) {
      setActive([0, newDirection])
    } else {
      setActive([requestedIndex, newDirection])
    }
  }
  return (
    <CaseStudyEntryRelatedStyled>
      <SemiBdTitle5 className="case-study-related__title">
        Similar Case Studies
      </SemiBdTitle5>

      {isMobile && (
        <>
          <AnimatePresence initial={false} custom={direction} mode="wait">
            <motion.div
              className="case-study-related__card-container"
              key={active}
              custom={direction}
              variants={variants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{
                x: { type: "spring", stiffness: 500, damping: 50 },
                opacity: { duration: 0.3 },
              }}
              drag={"x"}
              dragConstraints={{ left: 0, right: 0 }}
              dragElastic={1}
              onDragEnd={(_e, { offset, velocity }) => {
                const swipe = swipePower(offset.x, velocity.x)

                if (swipe < -swipeConfidenceThreshold) {
                  Navigate(1)
                } else if (swipe > swipeConfidenceThreshold) {
                  Navigate(-1)
                }
              }}
            >
              <EntryRelatedCard caseStudy={relatedEntries[active].node} />
            </motion.div>
          </AnimatePresence>
          <div className="case-study-related__navigation-display__container">
            {relatedEntries.map((_el, i) => {
              return (
                <div
                  className="case-study-related__navigation-display__dot"
                  onClick={() => Navigate(i)}
                  key={i}
                  style={{
                    backgroundColor:
                      i === active
                        ? "var(--color-gray600"
                        : "var(--color-gray200",
                  }}
                ></div>
              )
            })}
          </div>
        </>
      )}
      {!isMobile && (
        <div className="case-study-related__card-container">
          {relatedEntries.map((el, i) => {
            return <EntryRelatedCard caseStudy={el.node} key={i} />
          })}
        </div>
      )}
    </CaseStudyEntryRelatedStyled>
  )
}

export default CaseStudyEntryRelated

export const CaseStudyEntryRelatedStyled = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: ${rem(96)} ${rem(16)} 0;
  gap: ${rem(24)};
  width: 100%;
  overflow: hidden;
  min-height: ${rem(700)};

  @media ${media.midUp} {
    padding-bottom: ${rem(24)};
  }

  @media ${media.stMidUp} {
    gap: ${rem(48)};
  }

  .case-study-related__title {
    @media ${media.midUp} {
      padding-bottom: ${rem(16)};
      font-size: ${rem(24)};
    }

    @media ${media.stMidUp} {
      padding-bottom: 0;
    }
  }

  .case-study-related__card-container {
    position: absolute;
    margin-top: ${rem(60)};
    @media ${media.stMidUp} {
      margin-top: 0;
      position: static;
      display: flex;
      max-width: ${rem(1196)};
      gap: ${rem(48)};
    }
  }

  .case-study-related__navigation-display__container {
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
    height: ${rem(20)};
    gap: ${rem(10)};
    margin-top: ${rem(648)};

    @media ${media.midUp} {
      margin-top: ${rem(628)};
    }
  }

  .case-study-related__navigation-display__dot {
    border-radius: 50%;
    height: ${rem(8)};
    width: ${rem(8)};
    cursor: pointer;
  }
`
